// @ts-nocheck
// Paywall e telas de bloqueio. Reaproveita o estilo dark cerimonial mas
// agora separa os planos em 2 seções:
//   1. Pra leitores  (Leitor / Leitor PRO)
//   2. Pra criadores (Líder / Líder PRO)
//
// No web o checkout é o Mercado Pago — MENOS na variante das trilhas
// (interface nova), que desde 31/10/2026 só leva às lojas.
// Pagamento mensal: só cartão. Anual: cartão + Pix (com badge de desconto sutil).
import React, { useState, useEffect, useRef } from 'react';
import { Icon } from './icons';
import {
  Plan, PaymentMethod, BillingPeriod,
  PLAN_NAMES, PLAN_TAGLINES, PLAN_PRICES, PLAN_FEATURES, PLAN_GROUP,
  PLAN_RANK, getPlanRank, formatBRL, monthlyEquivalent, applyTermInFeature,
  SINGLE_PLAN, SINGLE_PLAN_PRICE_YEAR, SINGLE_PLAN_PRICE_MONTH, SINGLE_PLAN_YEAR_SAVING, SINGLE_PLAN_BADGE,
} from './plans';
import { PaywallStory } from './paywall-story';
import { openExternalUrl, isNativeApp, getPlatform } from './platform';
import { getOfferingPackages, purchaseRCPackage, restorePurchasesRC, RCPackage, esperarIdentidadeRC, identidadeRCPendente } from './revenuecat';
import { api } from '../services/apiV2';
import { LojasDoApp, LINHA_SO_LOJAS, linkDaLoja, plataformaDoAparelho } from './baixar-app';
import { modeloPago } from './nova/modelo';

// Nome da loja por plataforma — usado em toda copy de compra nativa (IAP).
// iOS → App Store · Android → Google Play. Em web cai no default (App Store),
// mas os blocos que usam isso só renderizam em nativo (isNativeApp()).
const storeName = (): string => (getPlatform() === 'android' ? 'Google Play' : 'App Store');
const storeTag = (): string => (getPlatform() === 'android' ? 'PAGAMENTO SEGURO · GOOGLE PLAY' : 'PAGAMENTO SEGURO · APP STORE');
// Onde o user gerencia/cancela a assinatura, por loja.
export const manageSubsCopy = (): string => (getPlatform() === 'android'
  ? 'Cancele a qualquer momento na Google Play Store > Assinaturas, até 24h antes do fim do período atual. O pagamento será cobrado pela Google Play.'
  : 'Cancele a qualquer momento em Ajustes > Apple ID > Assinaturas, até 24h antes do fim do período atual. O pagamento será cobrado pela App Store.');

// Cache em memória pra evitar refetch a cada open. TTL 5min — admin altera no
// console e em 5min refletir nos clients (ou recarrega manual).
let cachedPlans: any[] | null = null;
let cachedAt = 0;
const PLANS_TTL_MS = 5 * 60 * 1000;

async function loadPlans(session: any): Promise<any[]> {
  const now = Date.now();
  if (cachedPlans && (now - cachedAt) < PLANS_TTL_MS) return cachedPlans;
  try {
    const r = await api.listPlans(session);
    if (r?.plans?.length) {
      cachedPlans = r.plans;
      cachedAt = now;
      return r.plans;
    }
  } catch (e) {
    console.warn('[plans/api fallback aos defaults estáticos]', e);
  }
  return [];
}

// reader_plus removido — plano "Leitor" (R$5,40) ja absorveu todos os
// beneficios. Mantido como alias historico em plans.ts pra payloads antigos.
// PLANO ÚNICO — a vitrine vende só o SKU `leader` (preço de lançamento).
const PLAN_ORDER: Plan[] = [SINGLE_PLAN];
const READER_PLANS: Plan[] = [SINGLE_PLAN];
const LEADER_PLANS: Plan[] = [];

// ────────────────────────────────────────────────────────────────────
// IOSCheckoutButton — botão de compra via Apple IAP (RevenueCat).
//
// V2: NÃO mostra mais "Carregando preços". Preço vem hardcoded (do PLAN_PRICES
// que bate com App Store Connect). getOfferings só roda quando o user clica
// no botão — daí pega o RC Package real pra disparar a compra. Loading só
// durante a transação Apple.
// ────────────────────────────────────────────────────────────────────
// ────────────────────────────────────────────────────────────────────────
// DESCONTO DE INDICACAO — qual package pedir ao RevenueCat
//
// Os tres pares de produto convivem na mesma offering `default` e todos
// concedem o MESMO entitlement (leader_pro = acesso total). O que muda
// entre eles e so o preco. O tier vem do backend, nunca do client.
// ────────────────────────────────────────────────────────────────────────
// LEGADO — a escada de indicação acabou (14/09/2026). Todo degrau aponta para
// o plano único; o mapa sobrevive só porque `discount_tier` ainda chega do
// backend e algum payload antigo pode consultá-lo.
const PLANO_POR_TIER: Record<number, string> = {
  0: 'leader', 1: 'leader', 2: 'leader', 3: 'leader',
};

type TierInfo = {
  tier: number;
  /** Preço do tier em centavos — a MESMA fonte que o checkout cobra. */
  tierAnoCentavos: number | null;
  tierMesCentavos: number | null;
  planoPackage: string;
  precoCheioAnual: string;
  precoCheioMensal: string;
  aviso: string | null;
  carregando: boolean;
};

function useTierIndicacao(session: any): TierInfo {
  const [info, setInfo] = useState<TierInfo>({
    tier: 0, planoPackage: 'leader',
    tierAnoCentavos: null, tierMesCentavos: null,
    precoCheioAnual: 'R$ 29,90', precoCheioMensal: 'R$ 4,90',
    aviso: null, carregando: true,
  });

  useEffect(() => {
    if (!session) { setInfo(i => ({ ...i, carregando: false })); return; }
    let vivo = true;
    api.getAccess(session)
      .then((a: any) => {
        if (!vivo) return;
        const t = Math.max(0, Math.min(2, Number(a?.tier) || 0));
        setInfo({
          tier: t,
          planoPackage: PLANO_POR_TIER[t] || 'leader',
          tierAnoCentavos: t > 0 ? (a?.precos?.anual?.centavos ?? null) : null,
          tierMesCentavos: t > 0 ? (a?.precos?.mensal?.centavos ?? null) : null,
          precoCheioAnual: a?.precos?.cheio?.anual || 'R$ 29,90',
          precoCheioMensal: a?.precos?.cheio?.mensal || 'R$ 4,90',
          aviso: a?.avisoDesconto || null,
          carregando: false,
        });
      })
      .catch(() => { if (vivo) setInfo(i => ({ ...i, carregando: false })); });
    return () => { vivo = false; };
  }, [session]);

  return info;
}

export const IOSCheckoutButton: React.FC<{
  plan: Plan;
  period: BillingPeriod;
  onSuccess?: () => void;
  fallbackPriceLabel?: string;
  // Preço REAL vindo da loja (RevenueCat priceString). Quando presente, manda —
  // o rótulo nunca pode divergir do que a App Store/Play vai cobrar (Apple 2.3.1).
  storePriceLabel?: string;
  /** Abre a compra sozinho, uma vez, ao montar (a pessoa tocou "Assinar" na
   *  página de assinar e acabou de criar a conta — 02/11/2026). */
  autoIniciar?: boolean;
}> = ({ plan, period, onSuccess, fallbackPriceLabel, storePriceLabel, autoIniciar }) => {
  const [preparando, setPreparando] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const planLabel = PLAN_NAMES[plan];
  const targetIdentifier = `${plan}_${period === 'year' ? 'yearly' : 'monthly'}`;

  // Rótulo de PARTIDA, só para a tela não piscar vazia: o preço REAL da loja
  // sobrescreve assim que a RevenueCat responde (ver `priceLabel` abaixo).
  //
  // Os seis produtos seguem cadastrados nas duas lojas com preços diferentes —
  // é isso que permite trocar o valor de venda apontando `SINGLE_PLAN` para
  // outro SKU, sem repreçar produto na Apple/Google. Eles NÃO são níveis de
  // acesso: todo plano pago entrega o app inteiro.
  const IOS_PRICES: Record<string, string> = {
    'leader_pro_yearly':  'R$ 149,90',   // tier 0
    'leader_pro_monthly': 'R$ 19,90',
    'reader_yearly':      'R$ 79,90',    // tier 1
    'reader_monthly':     'R$ 9,90',
    'leader_yearly':      'R$ 29,90',    // tier 2
    'leader_monthly':     'R$ 4,90',
  };
  const priceLabel = storePriceLabel || IOS_PRICES[targetIdentifier] || fallbackPriceLabel || '';

  const handlePurchase = async () => {
    setPurchasing(true);
    setError(null);
    try {
      // MOB-18: a compra só sai depois de o RevenueCat saber quem é a conta
      // (logIn do boot). Prazo de 5 s e segue de qualquer jeito: nunca trava.
      if (identidadeRCPendente()) setPreparando(true);
      await esperarIdentidadeRC(5000);
      setPreparando(false);
      // Busca packages só agora (compra real). Se demorar, user já vê loading.
      const pkgs = await getOfferingPackages();
      const matching = pkgs.find(p => p.identifier === targetIdentifier);
      if (!matching) {
        setError('Produto indisponível na ' + storeName() + '. Tente novamente.');
        setPurchasing(false);
        return;
      }
      const result = await purchaseRCPackage((matching as any)._raw || matching);
      setPurchasing(false);
      if (result.ok) {
        onSuccess?.();
      } else if (!result.cancelled) {
        setError(result.error || 'Não foi possível concluir a compra');
      }
    } catch (e: any) {
      setError(e?.message || 'Erro inesperado');
      setPurchasing(false);
    }
  };
  const autoFeito = useRef(false);
  useEffect(() => {
    if (!autoIniciar || autoFeito.current) return;
    autoFeito.current = true;
    handlePurchase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoIniciar]);

  return (
    <div>
      <button
        onClick={handlePurchase}
        disabled={purchasing}
        style={{
          width: '100%', padding: '16px 24px',
          background: purchasing ? 'rgba(245,241,234,0.15)' : '#E87A3B',
          color: '#F5F1EA',
          border: 'none', borderRadius: 999,
          fontSize: 14, fontWeight: 600,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
          boxShadow: purchasing ? 'none' : '0 8px 24px -4px rgba(199,83,26,0.5)',
          cursor: purchasing ? 'default' : 'pointer',
          opacity: purchasing ? 0.7 : 1,
          fontFamily: 'inherit',
        }}>
        {purchasing
          ? (preparando ? 'Preparando…' : 'Conectando à ' + storeName() + '…')
          : <>Assinar {planLabel} · {priceLabel}</>}
      </button>
      <button
        onClick={async () => {
          const r = await restorePurchasesRC();
          if (r.ok && r.activeEntitlement) onSuccess?.();
        }}
        style={{
          width: '100%', marginTop: 10, padding: '10px',
          background: 'transparent', border: 'none',
          color: 'rgba(245,241,234,0.5)', fontSize: 11.5,
          cursor: 'pointer', fontFamily: 'inherit',
        }}>
        Restaurar compras anteriores
      </button>
      {error && (
        <div style={{
          marginTop: 8, padding: '10px 12px', borderRadius: 10,
          background: 'rgba(192,58,42,0.12)', color: '#C03A2A',
          fontSize: 12, textAlign: 'center',
        }}>{error}</div>
      )}
      <div style={{
        fontSize: 10, color: 'rgba(245,241,234,0.4)', textAlign: 'center',
        marginTop: 6, fontFamily: 'Geist Mono, monospace', letterSpacing: '0.1em',
      }}>{storeTag()}</div>
    </div>
  );
};


// O que o Pro libera, na variante das trilhas (refino de 30/10/2026). O resto
// do app é grátis na interface nova; aqui só entra o que a assinatura acrescenta.
const FEATURES_TRILHAS = [
  'Todas as trilhas do acervo, e as que entrarem enquanto a sua assinatura estiver ativa',
  'O Mentor acompanhando a sua leitura: comentários e insights que se abrem na página certa',
  'Desafios curtos para aplicar cada ideia no mesmo dia',
  'O caderno de aplicação de cada trilha, com as suas respostas guardadas',
];

export const PaywallScreen: React.FC<{
  onClose: () => void;
  onContactAdmin?: () => void;
  // Inicia checkout no Mercado Pago — redireciona pro init_point da assinatura
  onCheckout?: (plan: Plan, period: BillingPeriod, method: 'card' | 'pix', opts?: { cpfCnpj?: string }) => Promise<any>;
  currentPlan?: Plan;
  suggestedPlan?: Plan;
  featureLabel?: string;
  session?: any;
  // Quando true, escapa do app-shell (max-width:480) e usa todo o desktop.
  // Pra ser embedado na página /planos como standalone fora do app.
  standalone?: boolean;
  // Termo preferido pra "grupo" / "clube" (de profile.preferred_term).
  // Default 'clube' quando não informado.
  preferredTerm?: 'grupo' | 'clube';
  // Gate obrigatório pós-cadastro: esconde o X e troca por "Sair da conta".
  mandatory?: boolean;
  onLogout?: () => void;
  /** De onde veio quem abriu (vai no clique da loja): paywall | barra | planos. */
  origemDoClique?: string;
  /** 'trilhas' = a compra do Forjamente Pro na interface nova (refino de
   *  30/10/2026): o mesmo SKU e o mesmo checkout, com o texto das trilhas. */
  variante?: 'trilhas';
}> = ({ onClose, onContactAdmin, onCheckout, currentPlan = 'free', suggestedPlan, featureLabel, session, standalone, preferredTerm, mandatory, onLogout, origemDoClique = 'paywall', variante }) => {
  const trilhas = variante === 'trilhas';
  // Desconto de indicacao: decide QUAL package pedir e qual preco riscar.
  // O tier vem do backend (GET /api/me/access) — o client nunca calcula.
  const tierInfo = useTierIndicacao(session);
  // PREÇO ÚNICO (14/09/2026): o desconto por indicação acabou. O tier continua
  // vindo do backend porque `discount_tier` ainda existe no banco, mas ele não
  // muda mais preço nem package — todo mundo compra o mesmo SKU pelo mesmo
  // valor. Sem isto, três contas com tier antigo veriam "desconto aplicado"
  // com os dois preços idênticos dos dois lados do riscado.
  const planoDoTier = SINGLE_PLAN;
  const temDesconto = false;
  const precoCheioDoPeriodo = () =>
    (period === 'year' ? tierInfo.precoCheioAnual : tierInfo.precoCheioMensal);  // legado: temDesconto é sempre false
  const [period, setPeriod] = useState<BillingPeriod>('year');
  // Plano default sugerido: reader (que ja absorveu o antigo PRO).
  // Se backend mandar `reader_plus` por compat, normaliza pra `reader`.
  const initialSel: Plan = SINGLE_PLAN; // plano único — não há o que escolher
  const [selected, setSelected] = useState<Plan>(initialSel);
  const [remotePlans, setRemotePlans] = useState<any[]>([]);
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'pix'>('card');
  // NO SITE, O MERCADO PAGO É O CAMINHO SECUNDÁRIO (decisão do Wesley,
  // 02/10/2026: a venda é pelas lojas). Fica fechado atrás de "Prefere pagar
  // pelo site?" até a pessoa pedir.
  const [mpAberto, setMpAberto] = useState(false);
  const blocoLojasRef = useRef<HTMLDivElement | null>(null);
  const [checkingOut, setCheckingOut] = useState(false);
  // A altura da barra fixa de baixo, medida — ver o paddingBottom da tela.
  const barraFixaRef = useRef<HTMLDivElement | null>(null);
  const [alturaBarraFixa, setAlturaBarraFixa] = useState(0);
  useEffect(() => {
    const el = barraFixaRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const medir = () => setAlturaBarraFixa(Math.round(el.getBoundingClientRect().height));
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // Apple Guideline 3.1.1 / Google Play Payments: dentro do app nativo (iOS OU
  // Android) a compra de conteúdo digital TEM que passar pelo IAP da loja, não
  // por checkout web externo. Em nativo mostramos o botão IAP (RevenueCat); em
  // web, o checkout do Mercado Pago. isNativeApp() cobre iOS e Android.
  const isIosNative = isNativeApp();

  useEffect(() => { if (suggestedPlan) setSelected(suggestedPlan); }, [suggestedPlan]);

  // ── Preço REAL da loja (nativo) ─────────────────────────────────────
  // O app nativo carrega esta mesma web (capacitor server.url), então o rótulo
  // de preço TEM que vir da App Store/Play — se mostrarmos um valor e a loja
  // cobrar outro, é cobrança errada e reprovação na review (Apple 2.3.1).
  // Enquanto a resposta não chega, cai no valor estático.
  const [storePrices, setStorePrices] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!isIosNative) return;
    let cancelled = false;
    getOfferingPackages().then(pkgs => {
      if (cancelled || !Array.isArray(pkgs)) return;
      const m: Record<string, string> = {};
      pkgs.forEach((p: any) => {
        if (p?.identifier && p?.product?.priceString) m[p.identifier] = p.product.priceString;
      });
      setStorePrices(m);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [isIosNative]);

  const yearStoreLabel = storePrices[`${SINGLE_PLAN}_yearly`];
  const monthStoreLabel = storePrices[`${SINGLE_PLAN}_monthly`];
  // O desconto de indicação só existe no checkout WEB (Mercado Pago): nas
  // lojas o valor é o do produto cadastrado, e anunciar um preço que a Apple
  // não vai cobrar é violação direta da 2.3.1. Por isso o preço do tier entra
  // só quando NÃO é app nativo.
  //
  // Até aqui a vitrine ignorava o tier e mostrava sempre R$ 19,90 / R$ 149,90,
  // enquanto `aplicarDesconto` cobrava o preço com desconto — a tela prometia
  // um valor e o Mercado Pago cobrava outro. Agora as duas leem a mesma fonte.
  const yearLabel = yearStoreLabel || `R$ ${formatBRL(SINGLE_PLAN_PRICE_YEAR)}`;
  const monthLabel = monthStoreLabel || `R$ ${formatBRL(SINGLE_PLAN_PRICE_MONTH)}`;
  // Só afirmamos economia/equivalência quando o preço da loja bate com o nosso
  // (ou quando é web). Se a loja ainda não foi repreçada, calamos a conta.
  // A conta de "economize X" e "equivale a Y/mês" é feita em cima do preço
  // cheio. Com desconto de indicação ela deixaria de fechar, então some.
  // "Economize X" e "equivale a Y/mês" só podem ser ditos se o preço na tela
  // for mesmo o nosso. Em app nativo quem manda é a loja: enquanto o SKU não
  // responder o valor de lançamento, a conta fica calada em vez de errada.
  const precoLancamentoAnual = `R$ ${formatBRL(SINGLE_PLAN_PRICE_YEAR)}`;
  const priceInSync = !isIosNative
    || !yearStoreLabel
    || yearStoreLabel.replace(/[\s.]/g, '') === precoLancamentoAnual.replace(/[\s.]/g, '');

  // Busca planos do backend (cacheado 5min). Cai pros defaults estáticos
  // se a API falhar — não bloqueia render.
  useEffect(() => {
    let cancelled = false;
    loadPlans(session).then(plans => {
      if (!cancelled && plans.length > 0) setRemotePlans(plans);
    });
    return () => { cancelled = true; };
  }, [session]);

  // Mescla: o que veio da API substitui o default; falta = default.
  // Resolve placeholders {term} pelo termo preferido do user (grupo/clube).
  const planById = (id: Plan) => {
    const remote = remotePlans.find(p => p.planId === id);
    const rawFeatures = remote ? (remote.features || []) : (PLAN_FEATURES[id] || []);
    const features = rawFeatures.map((f: string) => applyTermInFeature(f, preferredTerm));
    if (remote) return {
      name: remote.displayName,
      tagline: remote.tagline,
      group: remote.group,
      price: { month: remote.monthPrice, year: remote.yearPrice,
        monthMethods: remote.monthMethods, yearMethods: remote.yearMethods,
        discountLabel: remote.discountLabel },
      features,
    };
    return {
      name: PLAN_NAMES[id],
      tagline: PLAN_TAGLINES[id],
      group: PLAN_GROUP[id],
      price: PLAN_PRICES[id],
      features,
    };
  };

  const currentRank = getPlanRank(currentPlan);

  return (
    <div className="scroll" style={{
      // O fim da página tem de passar da barra fixa (agora opaca) — que muda de
      // altura com "Sair da conta", o texto legal da loja, o desconto.
      paddingBottom: Math.max(temDesconto ? 210 : 140, alturaBarraFixa + 40), background: '#1A1614', color: '#F5F1EA', minHeight: '100vh',
      position: 'fixed', inset: 0, zIndex: 400,
      maxWidth: standalone ? undefined : 480,
      margin: '0 auto', overflowY: 'auto',
    }}>
      {!mandatory && (
        <button onClick={onClose} style={{
          position: 'fixed', top: 40, right: 20, zIndex: 10,
          width: 38, height: 38, borderRadius: '50%',
          background: 'rgba(245,241,234,0.1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#F5F1EA',
          border: 'none', cursor: 'pointer',
        }}><Icon name="close" size={standalone ? 18 : 16} color="#F5F1EA"/></button>
      )}

      {/* Header */}
      <div style={{ padding: '64px 24px 0', position: 'relative' }}>
        <div style={{
          position: 'absolute', top: 32, right: 40,
          width: 200, height: 200, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(199,83,26,0.4), transparent 70%)',
          filter: 'blur(34px)', pointerEvents: 'none',
        }}/>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px',
          background: 'linear-gradient(135deg, #C7531A, #E87A3B)', borderRadius: 999,
          fontFamily: 'Geist Mono, monospace', fontSize: 10, letterSpacing: '0.18em',
          textTransform: 'uppercase', fontWeight: 600, color: '#F5F1EA',
        }}>{trilhas ? 'Forjamente Pro' : 'Acesso completo'}</div>

        <div className="serif" style={{
          fontSize: 42, lineHeight: 1.06, marginTop: 18,
          letterSpacing: '-0.02em', maxWidth: 380,
        }} data-paywall-variante={variante || 'padrao'}>
          {trilhas ? (<>
            Todas as trilhas.<br/>
            Cada livro vira <span style={{ fontStyle: 'italic', color: '#E87A3B' }}>prática</span>.
          </>) : (<>
          Tudo destravado.<br/>
          Do primeiro livro ao <span style={{ fontStyle: 'italic', color: '#E87A3B' }}>hábito que fica</span>.
          </>)}
        </div>
        <div style={{ fontSize: 14.5, color: 'rgba(245,241,234,0.72)', marginTop: 14, lineHeight: 1.5, maxWidth: 380 }}>
          {trilhas
            ? 'Uma assinatura libera todas as trilhas do acervo: o Mentor acompanha a sua leitura e os desafios põem cada ideia em prática.'
            : <>Uma assinatura. O Forjamente inteiro na sua mão — pra quem já lê e quer ler
          mais, e pra quem ainda está começando.</>}
        </div>
      </div>

      {/* O que está incluído — listado UMA vez. Não há níveis: o acesso é o
          mesmo nos dois jeitos de pagar. */}
      <div style={{ padding: '26px 24px 0' }}>
        <div className="mono" style={{
          fontSize: 9.5, letterSpacing: '0.2em', textTransform: 'uppercase',
          color: '#E87A3B', fontWeight: 600, marginBottom: 14,
        }}>{trilhas ? 'O que o Pro libera' : 'Tudo isso, incluído'}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          {(trilhas ? FEATURES_TRILHAS : (PLAN_FEATURES[SINGLE_PLAN] || [])).map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <div style={{ marginTop: 2, flexShrink: 0 }}>
                <Icon name="check" size={13} color="#E87A3B" stroke={2.6}/>
              </div>
              <div style={{ fontSize: 14, lineHeight: 1.42, color: 'rgba(245,241,234,0.86)' }}>
                {applyTermInFeature(f, preferredTerm)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ASSINE PELO APP — só no site (no app nativo a compra já é pela loja). */}
      {!isIosNative && (
        <AssinePeloApp refBloco={blocoLojasRef} de={origemDoClique} trilhas={trilhas} precoAno={yearLabel} />
      )}

      {/* Escolha de pagamento — mesmo acesso, dois caminhos. */}
      <div style={{ padding: '30px 24px 0' }}>
        <div className="mono" style={{
          fontSize: 9.5, letterSpacing: '0.2em', textTransform: 'uppercase',
          color: 'rgba(245,241,234,0.45)', fontWeight: 600, marginBottom: 12,
        }}>{trilhas && !isIosNative ? 'O plano' : 'Escolha como pagar'}</div>

        {/* Tarja do lançamento. Fica FORA dos dois cards de propósito: vale
            para os dois jeitos de pagar, e repetida em cada um viraria ruído. */}
        {(trilhas || !!SINGLE_PLAN_BADGE) && (
          <div data-tarja-paywall style={{
            display: 'inline-block', marginBottom: 12,
            padding: '5px 12px', borderRadius: 999,
            background: 'rgba(232,112,42,0.16)', border: '1px solid rgba(232,112,42,0.45)',
            color: '#E8702A', fontSize: 10.5, fontWeight: 700,
            letterSpacing: '.08em', textTransform: 'uppercase',
          }}>{trilhas ? 'Preço de lançamento' : SINGLE_PLAN_BADGE}</div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <PayChoiceCard
            selected={period === 'year'}
            onSelect={() => { setPeriod('year'); setPaymentMethod('card'); }}
            badge={priceInSync && !trilhas ? `Economize R$ ${formatBRL(SINGLE_PLAN_YEAR_SAVING)}` : undefined}
            title="Anual"
            price={yearLabel}
            unit="por ano"
            sub={priceInSync ? `Equivale a R$ ${formatBRL(SINGLE_PLAN_PRICE_YEAR / 12)}/mês` : 'Acesso completo por 12 meses'}
            // O RODAPÉ DIZ O TRILHO QUE ESTÁ NA TELA (QA, 02/10/2026): pela loja
            // o anual renova; no site (Mercado Pago, "Prefere pagar pelo site?"
            // aberto) é um pagamento único de 12 meses, que não renova.
            foot={isIosNative ? 'Renova a cada 12 meses'
              : (mpAberto && !trilhas) ? 'No site: pagamento único de 12 meses · cartão ou Pix' : 'Pela loja, renova a cada 12 meses'}
          />
          {/* AS TRILHAS SÃO SÓ ANUAIS (decisão do Wesley, 31/10/2026): R$ 29,90 por
              ano libera todas as trilhas do acervo enquanto a assinatura estiver
              ativa, sem promessa de preço futuro (docs/forjamente-pro-oferta.md).
              Na variante das trilhas, o mensal não aparece. */}
          {!trilhas && <PayChoiceCard
            selected={period === 'month'}
            onSelect={() => { setPeriod('month'); setPaymentMethod('card'); }}
            title="Mensal"
            price={monthLabel}
            unit="por mês"
            sub="Sem compromisso de 12 meses"
            foot={isIosNative ? 'Renova todo mês'
              : mpAberto ? 'No site: assinatura mensal no cartão · cancele quando quiser' : 'Pela loja, renova todo mês'}
          />}
        </div>
        <div style={{
          fontSize: 12.5, lineHeight: 1.5, color: 'rgba(245,241,234,0.5)',
          textAlign: 'center', marginTop: 14,
        }} data-nota-periodo>
          {trilhas
            ? (modeloPago()
              ? 'O app inteiro e todas as trilhas, por 12 meses. Não assinar não apaga as suas notas nem o caderno.'
              : 'Todas as trilhas do acervo, por 12 meses. As notas e o caderno continuam grátis.')
            : 'O acesso é exatamente o mesmo nos dois. Muda só o intervalo da cobrança.'}
        </div>
      </div>

      {/* A narrativa — mini-landing com o Protocolo, a jornada e a evidência.
          Na variante das trilhas, a vitrine do Pro já contou a história. */}
      {!trilhas && <PaywallStory
        ctaLabel={period === 'year' ? `Assinar por ${yearLabel}/ano` : `Assinar por ${monthLabel}/mês`}
        priceHint={period === 'year'
          ? 'Plano anual · cancele quando quiser'
          : 'Plano mensal · cancele quando quiser'}
        onCta={() => {
          if (typeof document === 'undefined') return;
          window.scrollTo({ top: 0, behavior: 'smooth' });
          const sc = document.querySelector('.scroll');
          if (sc) sc.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      />}

      {/* CTA fixo. Web = pagamento ÚNICO (MP: cartão ou Pix à vista).
          Nativo = IAP (assinatura anual auto-renovável), com o texto legal que a
          loja exige. Sem garantia (fica na landing) e sem menção ao site (Apple). */}
      {/* A BARRA É OPACA DESDE O TOPO DELA.
          O fundo era um degradê que começava transparente e só fechava em 22%
          da altura — e o seletor Cartão/Pix ficava justamente nessa faixa, com
          botões de fundo translúcido: "Escolha como pagar" e a tarja do preço,
          rolando por baixo, apareciam ATRAVÉS do botão Cartão. O degradê
          continua, mas numa faixa ACIMA da barra, onde não há botão. */}
      <div ref={barraFixaRef} data-barra-paywall="1" style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, maxWidth: 480, margin: '0 auto',
        padding: '14px 24px 24px',
        background: '#1A1614',
      }}>
      <div aria-hidden="true" style={{
        position: 'absolute', left: 0, right: 0, top: -28, height: 28, pointerEvents: 'none',
        background: 'linear-gradient(180deg, rgba(26,22,20,0), #1A1614)',
      }} />
      {temDesconto && (
        <div style={{
          textAlign: 'center', marginBottom: 10, fontSize: 12.5,
          color: 'rgba(245,241,234,0.75)', lineHeight: 1.5,
        }}>
          <span style={{
            display: 'inline-block', padding: '3px 10px', borderRadius: 999,
            background: 'rgba(232,112,42,0.18)', border: '1px solid rgba(232,112,42,0.4)',
            color: '#E8702A', fontSize: 10.5, fontWeight: 700, letterSpacing: '.04em',
            textTransform: 'uppercase', marginBottom: 6,
          }}>Desconto de indicação aplicado</span>
          <div>
            de <span style={{ textDecoration: 'line-through', opacity: .6 }}>{precoCheioDoPeriodo()}</span>
          </div>
        </div>
      )}
      {isIosNative ? (
        <>
          <IOSCheckoutButton
            plan={planoDoTier}
            period={period}
            storePriceLabel={period === 'year' ? yearStoreLabel : monthStoreLabel}
            onSuccess={() => { setTimeout(() => { window.location.reload(); }, 300); }}
          />
          {/* Apple 3.1.2(c) / Google Play: disclosure de auto-renovação + links legais */}
          <div style={{
            fontSize: 10.5, lineHeight: 1.5, color: 'rgba(245,241,234,0.45)',
            textAlign: 'center', maxWidth: 380, margin: '12px auto 0',
          }}>
            Assinatura {period === 'year' ? 'anual' : 'mensal'}, renova automaticamente
            ao fim do período. {manageSubsCopy()}
            {tierInfo.aviso ? <><br/>{tierInfo.aviso}</> : null}
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 14, marginTop: 8, fontSize: 11 }}>
            <a href="https://forjamente.com.br/termos" target="_blank" rel="noopener noreferrer"
               style={{ color: 'rgba(245,241,234,0.6)', textDecoration: 'underline' }}>Termos de Uso</a>
            <span style={{ color: 'rgba(245,241,234,0.3)' }}>·</span>
            <a href="https://forjamente.com.br/privacidade" target="_blank" rel="noopener noreferrer"
               style={{ color: 'rgba(245,241,234,0.6)', textDecoration: 'underline' }}>Política de Privacidade</a>
          </div>
        </>
      ) : (
      <>
        {/* O CAMINHO PRINCIPAL NO SITE: a loja. No celular, vai direto para a
            loja do aparelho; no computador, rola até o QR e os selos. */}
        <a
          data-assinar-app="1"
          href={linkDaLoja(origemDoClique)}
          onClick={(e) => {
            if (plataformaDoAparelho() !== 'desktop') return;
            e.preventDefault();
            blocoLojasRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }}
          style={{
            width: '100%', padding: '16px 24px', boxSizing: 'border-box',
            background: '#E87A3B', color: '#F5F1EA', borderRadius: 999,
            fontSize: 15, fontWeight: 600, textDecoration: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            boxShadow: '0 8px 24px -4px rgba(199,83,26,0.5)', fontFamily: 'inherit',
          }}>
          Assinar pelo app
          <Icon name="arrow-right" size={15} color="#F5F1EA" stroke={2.2}/>
        </a>
        {/* Na variante das trilhas o site NÃO vende (31/10/2026): sem "Prefere
            pagar pelo site?" e sem o checkout do Mercado Pago. */}
        {!trilhas && <button
          data-mp-alternativa="1"
          onClick={() => setMpAberto((v) => !v)}
          style={{
            width: '100%', marginTop: 8, padding: '8px', background: 'transparent', border: 'none',
            color: 'rgba(245,241,234,0.55)', fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit',
            textDecoration: 'underline', textUnderlineOffset: 3,
          }}>{mpAberto ? 'Fechar o pagamento pelo site' : 'Prefere pagar pelo site? Cartão ou Pix'}</button>}
        {mpAberto && !trilhas && (<>
        {/* Método. Pix só existe no anual (pagamento único); mensal é recorrente
            no cartão. */}
        {period === 'year' && (
          <div style={{
            display: 'flex', gap: 6, marginBottom: 10,
            padding: 4, borderRadius: 12, background: 'rgba(245,241,234,0.06)',
          }}>
            <button onClick={() => setPaymentMethod('card')} style={{
              flex: 1, padding: '8px 10px', borderRadius: 8,
              background: paymentMethod === 'card' ? 'rgba(232,122,59,0.2)' : 'transparent',
              border: '1px solid ' + (paymentMethod === 'card' ? 'rgba(232,122,59,0.4)' : 'transparent'),
              color: paymentMethod === 'card' ? '#E87A3B' : 'rgba(245,241,234,0.7)',
              fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
            }}>💳 Cartão</button>
            <button onClick={() => setPaymentMethod('pix')} style={{
              flex: 1, padding: '8px 10px', borderRadius: 8,
              background: paymentMethod === 'pix' ? 'rgba(232,122,59,0.2)' : 'transparent',
              border: '1px solid ' + (paymentMethod === 'pix' ? 'rgba(232,122,59,0.4)' : 'transparent'),
              color: paymentMethod === 'pix' ? '#E87A3B' : 'rgba(245,241,234,0.7)',
              fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
            }}>💸 Pix à vista</button>
          </div>
        )}
        <button
          onClick={async () => {
            if (!onCheckout) { onContactAdmin?.(); return; }
            // SEM CPF. Era resto do gateway antigo (o Asaas exigia): pedido numa
            // caixa nativa do navegador e jogado fora — nada o levava ao
            // servidor. O Mercado Pago gera o Pix com o e-mail (medido em
            // 02/10/2026, sandbox: Pix criado sem CPF, com QR).
            setCheckingOut(true);
            try {
              await onCheckout(SINGLE_PLAN, period, period === 'year' ? paymentMethod : 'card');
            } catch (e) {
              setCheckingOut(false);
            }
          }}
          disabled={checkingOut}
          style={{
            width: '100%', padding: '16px 24px',
            background: checkingOut ? 'rgba(245,241,234,0.15)' : '#E87A3B',
            color: '#F5F1EA', border: 'none', borderRadius: 999,
            fontSize: 15, fontWeight: 600,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            boxShadow: checkingOut ? 'none' : '0 8px 24px -4px rgba(199,83,26,0.5)',
            cursor: checkingOut ? 'default' : 'pointer', fontFamily: 'inherit',
          }}>
          {checkingOut ? 'Redirecionando…' : (
            <>Assinar · {period === 'year' ? yearLabel : monthLabel}{period === 'year' ? '/ano' : '/mês'}
              <Icon name="arrow-right" size={15} color="#F5F1EA" stroke={2.2}/></>
          )}
        </button>
        <div style={{
          fontSize: 11, color: 'rgba(245,241,234,0.5)', textAlign: 'center', marginTop: 8, lineHeight: 1.4,
        }}>
          {period === 'year'
            ? 'Pagamento único anual · cartão ou Pix à vista'
            : 'Assinatura mensal no cartão · cancele quando quiser'}
        </div>
        </>)}
      </>
      )}
      {mandatory && (
        <button onClick={() => onLogout?.()} style={{
          width: '100%', marginTop: 12, padding: '10px',
          background: 'transparent', border: 'none',
          color: 'rgba(245,241,234,0.45)', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
        }}>Sair da conta</button>
      )}
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────
// ASSINE PELO APP — o caminho principal do site (02/10/2026).
//
// O faturamento vem das LOJAS (App Store / Google Play, via RevenueCat), que já
// cobram de verdade. A conta é a mesma: quem se cadastrou no site entra no app
// com o mesmo e-mail e assina pela loja. Tudo passa por /baixar, que leva à
// loja certa pelo aparelho e registra de onde veio o clique (ver
// server/lojas). Os utm_ da primeira visita seguem junto.
// ────────────────────────────────────────────────────────────────────────
// plataformaDoAparelho e linkDaLoja moram em baixar-app.tsx (com LojasDoApp).
//
// SÓ LOJAS (decisão do Wesley, 31/10/2026: "sem conta Mercado Pago por
// enquanto, assinatura apenas pelas lojas direto no app"). Na variante das
// trilhas (interface nova), o bloco mostra as DUAS lojas e não há pagamento
// pelo site.

const AssinePeloApp: React.FC<{ refBloco: React.MutableRefObject<HTMLDivElement | null>; de: string; trilhas?: boolean; precoAno?: string }> = ({ refBloco, de, trilhas, precoAno }) => {
  const plataforma = plataformaDoAparelho();
  if (trilhas) return (
    <div ref={refBloco} data-assine-pelo-app={plataforma} data-so-lojas="1" style={{ padding: '30px 24px 0' }}>
      <div style={{
        padding: '20px 18px', borderRadius: 18,
        background: 'rgba(232,122,59,0.10)', border: '1px solid rgba(232,122,59,0.35)',
      }}>
        <div className="mono" style={{
          fontSize: 9.5, letterSpacing: '0.2em', textTransform: 'uppercase',
          color: '#E87A3B', fontWeight: 600, marginBottom: 10,
        }}>Assine pelo app</div>
        <div data-linha-so-lojas style={{ fontSize: 15, lineHeight: 1.5, color: 'rgba(245,241,234,0.88)', marginBottom: 14 }}>
          {LINHA_SO_LOJAS} {precoAno} por ano.
        </div>
        <LojasDoApp de={de} escuro />
      </div>
    </div>
  );
  const selo = (loja: 'ios' | 'android', rotulo: string) => (
    <a key={loja} data-loja={loja} href={linkDaLoja(de, loja)} style={{
      flex: 1, minWidth: 130, display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '12px 14px', borderRadius: 12, background: '#F5F1EA', color: '#1A1614',
      fontSize: 14, fontWeight: 600, textDecoration: 'none',
    }}>{rotulo}</a>
  );
  return (
    <div ref={refBloco} data-assine-pelo-app={plataforma} style={{ padding: '30px 24px 0' }}>
      <div style={{
        padding: '20px 18px', borderRadius: 18,
        background: 'rgba(232,122,59,0.10)', border: '1px solid rgba(232,122,59,0.35)',
      }}>
        <div className="mono" style={{
          fontSize: 9.5, letterSpacing: '0.2em', textTransform: 'uppercase',
          color: '#E87A3B', fontWeight: 600, marginBottom: 10,
        }}>Assine pelo app</div>
        <div style={{ fontSize: 15, lineHeight: 1.5, color: 'rgba(245,241,234,0.88)' }}>
          Sua conta é a mesma: baixe o app, entre com o seu e-mail e assine pela loja
          {plataforma === 'ios' ? ' (App Store).' : plataforma === 'android' ? ' (Google Play).' : '.'}
        </div>
        {plataforma === 'desktop' ? (
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 16, flexWrap: 'wrap' }}>
            <div style={{ padding: 8, background: '#fff', borderRadius: 12, lineHeight: 0 }}>
              <img data-qr-loja="1" src={linkDaLoja(de, undefined, '/baixar/qr.svg')} alt="Código QR para baixar o app"
                width={132} height={132} style={{ display: 'block' }} />
            </div>
            <div style={{ flex: 1, minWidth: 160 }}>
              <div style={{ fontSize: 13, lineHeight: 1.5, color: 'rgba(245,241,234,0.7)', marginBottom: 10 }}>
                Aponte a câmera do celular: o código abre a loja certa.
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {selo('ios', 'App Store')}
                {selo('android', 'Google Play')}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            {plataforma === 'ios' ? selo('ios', 'Abrir na App Store') : selo('android', 'Abrir no Google Play')}
          </div>
        )}
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────
// PayChoiceCard — escolha do intervalo de cobrança (anual x mensal). O acesso
// é idêntico nos dois; por isso o card mostra só preço e condição, sem repetir
// a lista de benefícios.
// ────────────────────────────────────────────────────────────────────────
const PayChoiceCard: React.FC<{
  selected: boolean;
  onSelect: () => void;
  title: string;
  price: string;
  unit: string;
  sub: string;
  foot: string;
  badge?: string;
}> = ({ selected, onSelect, title, price, unit, sub, foot, badge }) => (
  <button
    onClick={onSelect}
    style={{
      position: 'relative', width: '100%', textAlign: 'left',
      padding: '16px 18px', borderRadius: 16,
      background: selected
        ? 'linear-gradient(135deg, rgba(199,83,26,0.20), rgba(232,122,59,0.06))'
        : 'rgba(245,241,234,0.04)',
      border: '1.5px solid ' + (selected ? '#E87A3B' : 'rgba(245,241,234,0.10)'),
      color: '#F5F1EA', cursor: 'pointer', fontFamily: 'inherit',
      transition: 'all 200ms',
    }}>
    {badge && (
      <span style={{
        position: 'absolute', top: -9, right: 14,
        background: 'linear-gradient(135deg, #4A8A5A, #5DAA70)', color: '#F5F1EA',
        padding: '3px 10px', borderRadius: 999,
        fontFamily: 'Geist Mono, monospace', fontSize: 8.5,
        letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 700,
        boxShadow: '0 4px 12px -4px rgba(74,138,90,0.5)',
      }}>{badge}</span>
    )}
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      {/* radio */}
      <div style={{
        width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
        border: '1.5px solid ' + (selected ? '#E87A3B' : 'rgba(245,241,234,0.3)'),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {selected && <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#E87A3B' }}/>}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="serif" style={{ fontSize: 19, lineHeight: 1.1 }}>{title}</div>
        <div style={{ fontSize: 12.5, color: 'rgba(245,241,234,0.6)', marginTop: 3 }}>{sub}</div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div className="serif" style={{ fontSize: 21, lineHeight: 1, color: selected ? '#E87A3B' : '#F5F1EA' }}>
          {price}
        </div>
        <div className="mono" style={{ fontSize: 9, color: 'rgba(245,241,234,0.5)', marginTop: 3, letterSpacing: '0.06em' }}>
          {unit}
        </div>
      </div>
    </div>
    <div style={{
      marginTop: 12, paddingTop: 10, borderTop: '1px solid rgba(245,241,234,0.08)',
      fontFamily: 'Geist Mono, monospace', fontSize: 10,
      letterSpacing: '0.06em', color: 'rgba(245,241,234,0.5)',
    }}>{foot}</div>
  </button>
);

// Item de explicação — usado na seção "Como funciona" do paywall pra esclarecer
// conceitos da tabela (livros simultâneos com clubes, privado vs público, etc).
const ExplanationItem: React.FC<{ title: string; body: string }> = ({ title, body }) => (
  <div style={{
    padding: '12px 14px',
    background: 'rgba(245,241,234,0.04)',
    borderRadius: 10,
    border: '1px solid rgba(245,241,234,0.08)',
  }}>
    <div style={{
      fontSize: 12.5, fontWeight: 600, color: '#F5F1EA', marginBottom: 4,
    }}>{title}</div>
    <div style={{
      fontSize: 12, lineHeight: 1.5, color: 'rgba(245,241,234,0.65)',
    }}>{body}</div>
  </div>
);

// Seção com header + cards de plano dela
const PlanSection: React.FC<{
  title: string;
  subtitle: string;
  plans: Plan[];
  selected: Plan;
  currentPlan: Plan;
  suggestedPlan?: Plan;
  period: BillingPeriod;
  onSelect: (p: Plan) => void;
  planById?: (id: Plan) => any;
}> = ({ title, subtitle, plans, selected, currentPlan, suggestedPlan, period, onSelect, planById }) => (
  <>
    <div style={{ padding: '32px 24px 0' }}>
      <div className="mono" style={{
        fontSize: 9.5, letterSpacing: '0.18em', textTransform: 'uppercase',
        color: '#E87A3B', fontWeight: 600,
      }}>{title}</div>
      <div className="serif" style={{ fontSize: 22, lineHeight: 1.1, marginTop: 4, color: '#F5F1EA' }}>
        {subtitle}
      </div>
    </div>
    <div style={{ padding: '14px 24px 0', display: 'flex', flexDirection: 'column', gap: 12 }}>
      {plans.map(plan => (
        <PlanCard
          key={plan}
          plan={plan}
          period={period}
          isSelected={selected === plan}
          isCurrent={plan === currentPlan}
          isSuggested={plan === suggestedPlan}
          onSelect={() => onSelect(plan)}
          override={planById?.(plan)}
        />
      ))}
    </div>
  </>
);

const PlanCard: React.FC<{
  plan: Plan;
  period: BillingPeriod;
  isSelected: boolean;
  isCurrent: boolean;
  isSuggested?: boolean;
  onSelect: () => void;
  // override = dados vindos do backend (admin editou). Se vazio, usa defaults estáticos.
  override?: { name: string; tagline: string; group: string; price: any; features: string[] };
}> = ({ plan, period, isSelected, isCurrent, isSuggested, onSelect, override }) => {
  const price = override?.price || PLAN_PRICES[plan];
  if (!price) return null;
  const displayName = override?.name || PLAN_NAMES[plan];
  const tagline = override?.tagline || PLAN_TAGLINES[plan];
  const features = override?.features || PLAN_FEATURES[plan];
  const monthlyEq = price.year ? price.year / 12 : null;
  const periodPrice = period === 'year' ? price.year : price.month;
  const methods = period === 'year' ? (price.yearMethods || []) : (price.monthMethods || []);
  const showDiscountBadge = period === 'year' && !!price.discountLabel;

  return (
    <button
      onClick={() => !isCurrent && onSelect()}
      disabled={isCurrent}
      style={{
        position: 'relative',
        padding: 18, borderRadius: 18,
        background: isSelected
          ? 'linear-gradient(135deg, rgba(199,83,26,0.22), rgba(232,122,59,0.10))'
          : 'rgba(245,241,234,0.04)',
        border: '1.5px solid ' + (isSelected ? '#E87A3B' : 'rgba(245,241,234,0.10)'),
        color: '#F5F1EA',
        textAlign: 'left',
        opacity: isCurrent ? 0.55 : 1,
        cursor: isCurrent ? 'default' : 'pointer',
        transition: 'all 220ms',
        fontFamily: 'inherit',
      }}
    >
      {/* Badges no topo */}
      <div style={{
        position: 'absolute', top: -8, left: 14, right: 14,
        display: 'flex', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {isSuggested && !isCurrent && (
            <span style={{
              background: '#E87A3B', color: '#F5F1EA',
              padding: '3px 9px', borderRadius: 999,
              fontFamily: 'Geist Mono, monospace',
              fontSize: 8.5, letterSpacing: '0.14em',
              textTransform: 'uppercase', fontWeight: 700,
            }}>recomendado</span>
          )}
          {isCurrent && (
            <span style={{
              background: 'rgba(245,241,234,0.15)', color: '#F5F1EA',
              padding: '3px 9px', borderRadius: 999,
              fontFamily: 'Geist Mono, monospace',
              fontSize: 8.5, letterSpacing: '0.14em',
              textTransform: 'uppercase', fontWeight: 600,
            }}>seu plano</span>
          )}
        </div>
        {showDiscountBadge && (
          <span style={{
            background: 'linear-gradient(135deg, #4A8A5A, #5DAA70)',
            color: '#F5F1EA',
            padding: '3px 9px', borderRadius: 999,
            fontFamily: 'Geist Mono, monospace',
            fontSize: 8.5, letterSpacing: '0.14em',
            textTransform: 'uppercase', fontWeight: 700,
            boxShadow: '0 4px 12px -4px rgba(74,138,90,0.5)',
          }}>{price.discountLabel} no anual</span>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="serif" style={{ fontSize: 22, lineHeight: 1.1 }}>
            {displayName}
          </div>
          <div className="mono" style={{
            fontSize: 9.5, color: 'rgba(245,241,234,0.55)',
            letterSpacing: '0.10em', textTransform: 'uppercase', marginTop: 4,
          }}>{tagline}</div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          {period === 'year' && monthlyEq ? (
            <>
              <div className="serif" style={{
                fontSize: 24, lineHeight: 1,
                color: isSelected ? '#E87A3B' : '#F5F1EA',
              }}>
                R$ {formatBRL(monthlyEq)}
              </div>
              <div className="mono" style={{
                fontSize: 9, color: 'rgba(245,241,234,0.5)',
                letterSpacing: '0.06em', marginTop: 3,
              }}>
                /mês · R$ {formatBRL(price.year)}/ano
              </div>
            </>
          ) : (
            <>
              <div className="serif" style={{
                fontSize: 24, lineHeight: 1,
                color: isSelected ? '#E87A3B' : '#F5F1EA',
              }}>
                R$ {formatBRL(periodPrice)}
              </div>
              <div className="mono" style={{
                fontSize: 9, color: 'rgba(245,241,234,0.5)',
                letterSpacing: '0.06em', marginTop: 3,
              }}>/mês</div>
            </>
          )}
        </div>
      </div>

      {/* Features list */}
      <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {features.map((feat, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <div style={{ marginTop: 2, flexShrink: 0 }}>
              <Icon name="check" size={11} color={isSelected ? '#E87A3B' : 'rgba(245,241,234,0.5)'} stroke={2.4}/>
            </div>
            <div style={{ fontSize: 12.5, lineHeight: 1.35, color: 'rgba(245,241,234,0.85)' }}>
              {feat}
            </div>
          </div>
        ))}
      </div>

      {/* Métodos de pagamento (sutil, no rodapé) */}
      <div style={{
        marginTop: 14, paddingTop: 12,
        borderTop: '1px solid rgba(245,241,234,0.08)',
        display: 'flex', alignItems: 'center', gap: 8,
        fontSize: 10.5, color: 'rgba(245,241,234,0.55)',
        fontFamily: 'Geist Mono, monospace', letterSpacing: '0.06em',
      }}>
        {methods.includes('card') && <span>💳 Cartão</span>}
        {methods.includes('pix') && <span>· 💸 Pix</span>}
        {!methods.includes('pix') && period === 'month' && (
          <span style={{ marginLeft: 'auto', color: 'rgba(245,241,234,0.4)' }}>
            anual libera Pix
          </span>
        )}
      </div>
    </button>
  );
};

export const UpgradeCelebration: React.FC<{ open: boolean; onClose: () => void; plan?: Plan }> = ({ open, onClose, plan = 'reader' }) => {
  if (!open) return null;
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 500,
      background: '#1A1614', color: '#F5F1EA',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: 40, textAlign: 'center',
      animation: 'fadeIn 400ms ease-out',
      maxWidth: 480, margin: '0 auto',
    }}>
      <div style={{
        width: 100, height: 100, borderRadius: '50%',
        background: 'radial-gradient(circle, #E87A3B, #8a2f11)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 0 60px rgba(199,83,26,0.6)',
      }}>
        <Icon name="sparkle" size={44} color="#F5F1EA" stroke={2}/>
      </div>
      <div className="mono" style={{ fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#E87A3B', marginTop: 30 }}>Bem-vindo ao</div>
      <div className="serif" style={{ fontSize: 50, lineHeight: 1, marginTop: 10, letterSpacing: '-0.02em' }}>
        Forjamente <span style={{ fontStyle: 'italic', color: '#E87A3B' }}>{PLAN_NAMES[plan]}</span>.
      </div>
      <div style={{ fontSize: 14, color: 'rgba(245,241,234,0.7)', marginTop: 14, lineHeight: 1.5, maxWidth: 280 }}>
        Tudo destravado. Seu hábito agora é completo.
      </div>
      <button onClick={onClose} style={{
        marginTop: 40, padding: '16px 32px',
        background: '#F5F1EA', color: '#1A1614',
        border: 'none', borderRadius: 999, fontSize: 15, fontWeight: 500,
        display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
      }}>
        Voltar a ler
        <Icon name="arrow-right" size={14} color="#1A1614" stroke={2.2}/>
      </button>
    </div>
  );
};

export const LockedFeatureScreen: React.FC<{
  title: string;
  subtitle: string;
  onUpgrade: () => void;
  requiredPlan?: Plan;
  currentPlan?: Plan;
}> = ({ title, subtitle, onUpgrade, requiredPlan = SINGLE_PLAN, currentPlan = 'free' }) => {
  const planLabel = PLAN_NAMES[SINGLE_PLAN];
  const price = PLAN_PRICES[SINGLE_PLAN];
  return (
    <div className="scroll" style={{ paddingBottom: 120 }}>
      <div style={{ padding: '40px 20px 0' }}>
        <div style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>{subtitle}</div>
        <div className="serif" style={{ fontSize: 36, lineHeight: 1.05, marginTop: 14, letterSpacing: '-0.01em' }}>
          {title}
        </div>
      </div>
      <div style={{ padding: '30px 20px 0' }}>
        <div style={{
          background: 'var(--paper-2)', border: '1px solid var(--line)',
          borderRadius: 22, padding: '26px 22px',
        }}>
          <div style={{
            width: 56, height: 56, margin: '0 auto 14px',
            borderRadius: '50%', background: 'var(--ember)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 8px 24px -4px rgba(199,83,26,0.5)',
          }}><Icon name="flame" size={26} color="var(--paper)" stroke={2}/></div>

          <div className="serif" style={{ fontSize: 22, lineHeight: 1.2, textAlign: 'center' }}>
            Isso faz parte do <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>acesso completo</span>.
          </div>

          <div style={{ marginTop: 20, padding: 16, borderRadius: 16, background: 'var(--ember-soft)', border: '1px solid var(--ember)' }}>
            <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.45 }}>
              Com o <strong>{planLabel}</strong>, o app inteiro fica aberto:
            </div>
            {[
              'Leitura e clubes sem limite',
              'O Protocolo do Hábito completo',
              'Ferramentas de líder',
            ].map((f, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 7 }}>
                <span style={{ color: 'var(--ember-2)', fontSize: 15, fontWeight: 700, lineHeight: 1 }}>∞</span>
                <span style={{ fontSize: 13.5, color: 'var(--ink)', lineHeight: 1.35 }}>{f}</span>
              </div>
            ))}
          </div>

          {isNativeApp() ? (
            <div style={{ marginTop: 18 }}>
              <IOSCheckoutButton
                plan={SINGLE_PLAN}
                period="year"
                onSuccess={() => { setTimeout(() => window.location.reload(), 300); }}
              />
            </div>
          ) : (
            <button onClick={onUpgrade} style={{
              width: '100%', marginTop: 18, padding: '16px',
              background: 'var(--ember)', color: 'var(--paper)',
              borderRadius: 999, fontSize: 14.5, fontWeight: 600,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              border: 'none', cursor: 'pointer', fontFamily: 'inherit',
              boxShadow: '0 10px 24px -8px rgba(199,83,26,0.5)',
            }}>
              Ver o plano completo
              <Icon name="arrow-right" size={15} color="var(--paper)" stroke={2.2}/>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────
// PaywallModal — versão SLIM que substitui o paywall full-page dentro do app.
//
// V4 (plano único): não há mais níveis nem catálogo. Nativo mostra o card do
// acesso completo com os dois intervalos; web manda pro /planos.
// ────────────────────────────────────────────────────────────────────────
export const PaywallModal: React.FC<{
  open: boolean;
  onClose: () => void;
  currentPlan?: Plan;
  suggestedPlan?: Plan;
  featureLabel?: string;
  /** Sem sessão não há como saber o tier — e a folha volta a cobrar cheio. */
  session?: any;
}> = ({ open, onClose, currentPlan = 'free', suggestedPlan, featureLabel, session }) => {
  const [period, setPeriod] = useState<BillingPeriod>('year');
  // Hook ANTES do early return: React não aceita hook condicional.
  const tierInfo = useTierIndicacao(session || null);
  if (!open) return null;

  // Esta folha mostrava sempre leader_pro. Como o identificador do produto
  // sai daqui (`<plano>_<periodo>`), quem tinha desconto era mandado para o
  // produto de preço cheio na loja — o desconto existia na App Store Connect
  // e nunca era pedido. O nome na tela continua o do plano único: é o mesmo
  // produto, o que muda é o degrau de preço.
  const target = SINGLE_PLAN;   // preço único — o tier não escolhe mais package
  const targetName = PLAN_NAMES[SINGLE_PLAN];

  const goToPlans = () => {
    if (typeof window === 'undefined') return;
    const url = window.location.origin + '/planos' + (featureLabel ? '?feat=' + encodeURIComponent(featureLabel) : '');
    openExternalUrl(url);
  };

  // Plano único. Os preços aqui são só rótulo de partida — o PlanCardIOS
  // sobrescreve com o preço REAL da loja (RevenueCat) assim que ele chega.
  type IOSPlanCard = {
    id: Plan;
    name: string;
    tagline: string;
    priceMonth: string;
    priceYear: string;
    features: string[];
    highlight?: boolean;
  };
  const IOS_PLANS: IOSPlanCard[] = [
    {
      id: target,
      name: targetName,
      tagline: 'Acesso completo ao Forjamente',
      priceMonth: `R$ ${formatBRL(SINGLE_PLAN_PRICE_MONTH)}`,
      priceYear: `R$ ${formatBRL(SINGLE_PLAN_PRICE_YEAR)}`,
      features: [
        'Leitura e clubes sem limite',
        'O Protocolo do Hábito completo',
        'Ferramentas de líder',
      ],
      highlight: true,
    },
  ];

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 500,
        background: 'rgba(10,8,6,0.7)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        animation: 'pwfade 200ms ease-out',
      }}
    >
      <style>{`
        @keyframes pwfade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes pwslide { from { transform: translateY(100%) } to { transform: translateY(0) } }
      `}</style>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 460,
          maxHeight: '92vh',
          overflowY: 'auto',
          background: '#1A1614', color: '#F5F1EA',
          borderTopLeftRadius: 24, borderTopRightRadius: 24,
          padding: '24px 22px 24px',
          boxShadow: '0 -20px 60px rgba(0,0,0,0.5)',
          animation: 'pwslide 280ms cubic-bezier(0.22, 1, 0.36, 1)',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
        }}
      >
        {/* Handle */}
        <div style={{
          width: 40, height: 4, borderRadius: 2,
          background: 'rgba(245,241,234,0.2)',
          margin: '0 auto 18px',
        }}/>

        {/* Título */}
        <div className="serif" style={{
          fontSize: 24, lineHeight: 1.15, marginBottom: 4,
          color: '#F5F1EA',
        }}>
          {featureLabel ? <>
            Pra <span style={{ fontStyle: 'italic', color: '#E87A3B' }}>{featureLabel.toLowerCase()}</span>
          </> : (
            <>Escolha seu <span style={{ fontStyle: 'italic', color: '#E87A3B' }}>plano</span></>
          )}
        </div>
        <div style={{
          fontSize: 12.5, lineHeight: 1.5, color: 'rgba(245,241,234,0.6)',
          marginBottom: 16,
        }}>
          {isNativeApp()
            ? 'Pagamento via ' + storeName() + '. Cancele a qualquer momento nos ajustes de assinatura da loja.'
            : <>Faça upgrade pelo navegador pra continuar.</>}
        </div>

        {isNativeApp() ? (
          <>
            {/* Toggle Mensal / Anual */}
            <div style={{
              display: 'flex', gap: 4, padding: 4,
              background: 'rgba(245,241,234,0.06)',
              borderRadius: 12, marginBottom: 16,
            }}>
              {(['month', 'year'] as BillingPeriod[]).map(p => (
                <button
                  key={p}
                  onClick={() => setPeriod(p)}
                  style={{
                    flex: 1, padding: '9px 12px', borderRadius: 9,
                    background: period === p ? '#E87A3B' : 'transparent',
                    color: period === p ? '#F5F1EA' : 'rgba(245,241,234,0.65)',
                    border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                    fontSize: 13, fontWeight: 600,
                  }}
                >
                  {p === 'month' ? 'Mensal' : 'Anual'}
                  {p === 'year' && (
                    <span style={{
                      marginLeft: 6, fontSize: 10,
                      color: period === 'year' ? 'rgba(255,255,255,0.85)' : 'rgba(245,241,234,0.5)',
                    }}>· economiza ~37%</span>
                  )}
                </button>
              ))}
            </div>

            {/* Cards dos 3 planos */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {IOS_PLANS.map(p => (
                <PlanCardIOS
                  key={p.id}
                  plan={p}
                  period={period}
                  isCurrent={currentPlan === p.id}
                  isSuggested={target === p.id}
                  onSuccess={() => { setTimeout(() => window.location.reload(), 300); }}
                />
              ))}
            </div>

            {/* Restaurar compras */}
            <button
              onClick={async () => {
                const r = await restorePurchasesRC();
                if (r.ok && r.activeEntitlement) {
                  setTimeout(() => window.location.reload(), 300);
                }
              }}
              style={{
                width: '100%', marginTop: 12, padding: '10px',
                background: 'transparent', border: 'none',
                color: 'rgba(245,241,234,0.55)', fontSize: 12,
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              Restaurar compras anteriores
            </button>
          </>
        ) : (
          <button
            onClick={goToPlans}
            style={{
              width: '100%', padding: '16px',
              background: '#E87A3B', color: '#F5F1EA',
              border: 'none', borderRadius: 14,
              fontSize: 14, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              marginTop: 6,
            }}
          >
            Ver planos
            <Icon name="arrow-right" size={14} color="#F5F1EA" stroke={2.2}/>
          </button>
        )}

        {/* Fechar */}
        <button
          onClick={onClose}
          style={{
            width: '100%', marginTop: 10, padding: '10px',
            background: 'transparent', border: 'none',
            color: 'rgba(245,241,234,0.5)', fontSize: 12.5,
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          Agora não
        </button>

        {/* Apple 3.1.1/3.1.2(c) e Google Play: links pra EULA e Privacy Policy
            sao obrigatorios em apps com auto-renewable subscriptions */}
        {isNativeApp() && (
          <div style={{
            display: 'flex', justifyContent: 'center', gap: 14,
            marginTop: 10, fontSize: 11, lineHeight: 1.4,
          }}>
            <a
              href="https://forjamente.com.br/termos"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'rgba(245,241,234,0.65)', textDecoration: 'underline' }}
            >Termos de Uso</a>
            <span style={{ color: 'rgba(245,241,234,0.3)' }}>·</span>
            <a
              href="https://forjamente.com.br/privacidade"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'rgba(245,241,234,0.65)', textDecoration: 'underline' }}
            >Politica de Privacidade</a>
          </div>
        )}

        {/* Resumo legal das subscriptions auto-renovaveis (Apple 3.1.2(c) / Google Play) */}
        {isNativeApp() && (
          <div style={{
            fontSize: 10.5, lineHeight: 1.5, color: 'rgba(245,241,234,0.45)',
            marginTop: 12, textAlign: 'center', maxWidth: 380, margin: '12px auto 0',
          }}>
            Assinatura renova automaticamente ao fim de cada periodo (mensal ou anual).
            {' '}{manageSubsCopy()}
          </div>
        )}

        <div style={{
          fontSize: 9.5, color: 'rgba(245,241,234,0.35)',
          textAlign: 'center', marginTop: 10,
          fontFamily: 'Geist Mono, monospace', letterSpacing: '0.12em',
        }}>
          {isNativeApp() ? storeTag() : 'PAGAMENTO SEGURO · MERCADO PAGO'}
        </div>
      </div>
    </div>
  );
};

// Card de plano dentro do PaywallModal (iOS only)
const PlanCardIOS: React.FC<{
  plan: {
    id: Plan;
    name: string;
    tagline: string;
    priceMonth: string;
    priceYear: string;
    features: string[];
    highlight?: boolean;
  };
  period: BillingPeriod;
  isCurrent: boolean;
  isSuggested: boolean;
  onSuccess: () => void;
}> = ({ plan, period, isCurrent, isSuggested, onSuccess }) => {
  const [purchasing, setPurchasing] = useState(false);
  const [preparando, setPreparando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const periodLabel = period === 'month' ? '/mês' : '/ano';
  const targetIdentifier = `${plan.id}_${period === 'year' ? 'yearly' : 'monthly'}`;

  // Preço REAL da loja manda sobre o rótulo estático (Apple 2.3.1 — o app
  // nativo roda esta mesma web, então nunca pode anunciar valor divergente).
  const [storePrices, setStorePrices] = useState<Record<string, string>>({});
  useEffect(() => {
    let cancelled = false;
    getOfferingPackages().then(pkgs => {
      if (cancelled || !Array.isArray(pkgs)) return;
      const m: Record<string, string> = {};
      pkgs.forEach((p: any) => {
        if (p?.identifier && p?.product?.priceString) m[p.identifier] = p.product.priceString;
      });
      setStorePrices(m);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const priceLabel = storePrices[targetIdentifier]
    || (period === 'month' ? plan.priceMonth : plan.priceYear);

  const handlePurchase = async () => {
    if (isCurrent || purchasing) return;
    // Log: qual plano user clicou
    try {
      fetch('/api/debug-log', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ tag:'rc:click', data: { planId: plan.id, planName: plan.name, period, targetIdentifier } }) }).catch(()=>{});
    } catch {}
    setPurchasing(true);
    setError(null);
    try {
      // MOB-18: espera o logIn do RevenueCat (prazo 5 s; nunca trava a compra).
      if (identidadeRCPendente()) setPreparando(true);
      await esperarIdentidadeRC(5000);
      setPreparando(false);
      const pkgs = await getOfferingPackages();
      try {
        fetch('/api/debug-log', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ tag:'rc:click_pkgs', data: { available: pkgs.map(p=>p.identifier), target: targetIdentifier } }) }).catch(()=>{});
      } catch {}
      const matching = pkgs.find(p => p.identifier === targetIdentifier);
      if (matching) {
        try {
          fetch('/api/debug-log', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ tag:'rc:click_matched', data: { id: matching.identifier, productId: matching.product?.identifier, rawProductId: (matching as any)._raw?.product?.identifier } }) }).catch(()=>{});
        } catch {}
      }
      if (!matching) {
        setError('Produto indisponível');
        setPurchasing(false);
        return;
      }
      const result = await purchaseRCPackage((matching as any)._raw || matching);
      setPurchasing(false);
      if (result.ok) onSuccess();
      else if (!result.cancelled) setError(result.error || 'Erro');
    } catch (e: any) {
      setError(e?.message || 'Erro');
      setPurchasing(false);
    }
  };

  const borderColor = isSuggested ? 'rgba(232,122,59,0.45)' : 'rgba(245,241,234,0.1)';
  const bgColor = isSuggested ? 'rgba(232,122,59,0.05)' : 'rgba(245,241,234,0.03)';

  return (
    <div style={{
      padding: '14px 16px', borderRadius: 14,
      background: bgColor,
      border: `1px solid ${borderColor}`,
      position: 'relative',
    }}>
      {plan.highlight && !isCurrent && (
        <div style={{
          position: 'absolute', top: -8, right: 14,
          padding: '2px 8px', borderRadius: 999,
          background: '#E87A3B', color: '#F5F1EA',
          fontSize: 9.5, fontWeight: 600,
          letterSpacing: '0.06em',
        }}>MAIS COMUM</div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
        <div className="serif" style={{ fontSize: 18, color: '#F5F1EA' }}>{plan.name}</div>
        <div style={{ fontSize: 14, color: '#E87A3B', fontWeight: 600 }}>
          {priceLabel}<span style={{ fontSize: 11, color: 'rgba(245,241,234,0.5)', fontWeight: 400 }}>{periodLabel}</span>
        </div>
      </div>
      <div style={{ fontSize: 11.5, color: 'rgba(245,241,234,0.55)', marginBottom: 10 }}>
        {plan.tagline}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 }}>
        {plan.features.map((f, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
            <Icon name="check" size={10} color="#E87A3B" stroke={2.4}/>
            <div style={{ fontSize: 11.5, color: 'rgba(245,241,234,0.8)', lineHeight: 1.35 }}>{f}</div>
          </div>
        ))}
      </div>
      <button
        onClick={handlePurchase}
        disabled={isCurrent || purchasing}
        style={{
          width: '100%', padding: '11px 14px', borderRadius: 999,
          background: isCurrent ? 'rgba(245,241,234,0.1)' : (purchasing ? 'rgba(232,122,59,0.4)' : '#E87A3B'),
          color: '#F5F1EA',
          border: 'none', fontSize: 13, fontWeight: 600,
          cursor: isCurrent || purchasing ? 'default' : 'pointer',
          fontFamily: 'inherit',
        }}>
        {isCurrent ? 'Plano atual' : purchasing ? (preparando ? 'Preparando…' : 'Conectando à ' + storeName() + '…') : `Assinar ${plan.name}`}
      </button>
      {error && (
        <div style={{
          marginTop: 6, padding: '6px 8px', borderRadius: 8,
          background: 'rgba(192,58,42,0.12)', color: '#C03A2A',
          fontSize: 10.5, textAlign: 'center',
        }}>{error}</div>
      )}
    </div>
  );
};


// ────────────────────────────────────────────────────────────────────────
// PlansStandaloneRoute — wrapper pra renderizar o PaywallScreen como página
// standalone em /planos. Usado quando window.location.pathname === '/planos'.
//
// Diferenças do PaywallScreen-modal-no-app:
//   - escapa do app-shell (sem max-width:480)
//   - "fechar" volta pra raiz com history.back() ou go to /
//   - URL params: ?suggest=reader_plus&feat=Caixa+de+perguntas
// ────────────────────────────────────────────────────────────────────────
export const PlansStandaloneRoute: React.FC<{
  session: any;
  currentPlan?: Plan;
  preferredTerm?: 'grupo' | 'clube';
  onCheckout?: (plan: Plan, period: BillingPeriod, method: 'card' | 'pix', opts?: { cpfCnpj?: string }) => Promise<any>;
}> = ({ session, currentPlan = 'free', preferredTerm, onCheckout }) => {
  const [params] = useState(() => {
    if (typeof window === 'undefined') return new URLSearchParams();
    return new URLSearchParams(window.location.search);
  });
  // Sanitiza: aceita só plan IDs válidos. Quando vem "[object Object]" ou outra
  // string suja, ignora pra não passar adiante como suggestedPlan.
  const rawSuggest = params.get('suggest') || '';
  // Aceita reader_plus por compat (URLs antigas) — normaliza pra reader.
  const normalizedSuggest = rawSuggest === 'reader_plus' ? 'reader' : rawSuggest;
  const suggested = (['reader','leader','leader_pro'].includes(normalizedSuggest))
    ? (normalizedSuggest as Plan)
    : undefined;
  const featLabel = params.get('feat') || undefined;

  return (
    <PaywallScreen
      standalone
      origemDoClique="planos"
      session={session}
      currentPlan={currentPlan}
      preferredTerm={preferredTerm}
      suggestedPlan={suggested}
      featureLabel={featLabel}
      onClose={() => {
        // Se veio de outra aba (window.opener), fecha. Senão volta pra raiz.
        if (typeof window !== 'undefined') {
          if (window.opener) {
            window.close();
          } else {
            window.location.href = '/';
          }
        }
      }}
      onCheckout={onCheckout}
    />
  );
};
